<template>
  <div>
    <PageHeader
      title="Targets"
      subtitle="Primary care practices in your territory, ranked by how much they can gain with you."
      :heading-level="1"
    >
      <template #actions>
        <FilterDropdown
          label="Territory"
          :icon="['fas', 'map']"
          :model-value="territory"
          :options="territoryOptions"
          placeholder="All states"
          clearable
          @update:model-value="onTerritory"
        />
      </template>
    </PageHeader>

    <div class="px-6 py-4">
      <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div class="flex flex-wrap items-start gap-4 px-4 py-3 border-b border-gray-100">
          <FilterTabs
            label="Show"
            :model-value="targetsOnly ? 'targets' : 'all'"
            :options="showOptions"
            @update:model-value="targetsOnly = $event === 'targets'"
          />
          <FilterTabs
            label="Can improve"
            :model-value="verdictFilter"
            :options="verdictOptions"
            @update:model-value="verdictFilter = $event"
          />
          <FilterDropdown
            label="In an ACO today"
            :model-value="acoFilter"
            :options="acoOptions"
            placeholder="Any"
            clearable
            @update:model-value="acoFilter = $event"
          />
          <FilterDropdown
            label="Medicare patients"
            :model-value="sizeFilter"
            :options="sizeOptions"
            placeholder="Any size"
            clearable
            @update:model-value="sizeFilter = $event"
          />
          <button v-if="filtersActive" type="button" class="btn-link text-sm mt-6" @click="resetFilters">Reset</button>
        </div>

        <div class="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
          <span><span class="font-semibold text-gray-900">{{ filtered.length }}</span> shown</span>
          <span><span class="font-semibold text-gray-900">{{ targetCount }}</span> targets of {{ rows.length }} briefed practices</span>
          <span><span class="font-semibold text-gray-900">{{ inAcoCount }}</span> already in an ACO</span>
          <span v-if="buildId" class="ml-auto text-gray-400">Graph build {{ buildId.slice(0, 8) }}</span>
        </div>

        <LoadingIndicator v-if="loading" message="Loading targets..." />
        <div v-else-if="!rows.length" class="px-4 py-12 text-center">
          <div class="text-sm font-semibold text-gray-900">No briefs yet</div>
          <p class="text-sm text-gray-600 mt-1">Briefs are written by the growth brief batch. Once it has run for this territory, targets appear here.</p>
        </div>
        <div v-else-if="!filtered.length" class="px-4 py-12 text-center">
          <div class="text-sm font-semibold text-gray-900">Nothing matches these filters</div>
          <button type="button" class="btn-link text-sm mt-2" @click="resetFilters">Reset filters</button>
        </div>
        <AgGridVue
          v-else
          class="targets-grid"
          :theme="gridTheme"
          :column-defs="columnDefs"
          :default-col-def="defaultColDef"
          :row-data="filtered"
          :row-height="48"
          :header-height="40"
          dom-layout="autoHeight"
          :suppress-cell-focus="true"
          :get-row-id="getRowId"
          row-class="cursor-pointer"
          @row-clicked="onRowClicked"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3'
import { themeQuartz } from 'ag-grid-community'
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue'
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue'
import FilterTabs from '@/components/widgetcomponents/library/FilterTabs.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'

const VERDICT_DOT = {
  fixable: 'bg-secondary-800',
  partly: 'bg-yellow-dark',
  stuck: 'bg-gray-300',
}
const VERDICT_LABEL = { fixable: 'Fixable', partly: 'Partly', stuck: 'Stuck' }
const INITIATIVE_LABEL = {
  awv: 'Wellness visits',
  ccm: 'Chronic care management',
  tcm: 'Follow up after discharge',
  acp: 'Advance care planning',
  rpm: 'Remote monitoring',
}

function fmtMoney(v) {
  if (v == null) return ''
  if (v >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M'
  if (v >= 1000) return '$' + Math.round(v / 1000) + 'K'
  return '$' + Math.round(v)
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}

export default {
  name: 'TargetsTab',
  components: { AgGridVue, PageHeader, FilterDropdown, FilterTabs, LoadingIndicator },
  props: {
    initialTerritory: { type: String, default: null },
  },
  emits: ['open-brief', 'territory-change'],
  data() {
    return {
      rows: [],
      states: [],
      buildId: '',
      loading: false,
      territory: this.initialTerritory,
      targetsOnly: true,
      showOptions: [
        { value: 'targets', label: 'Targets' },
        { value: 'all', label: 'All practices' },
      ],
      verdictFilter: 'all',
      verdictOptions: [
        { value: 'all', label: 'All' },
        { value: 'fixable', label: 'Fixable' },
        { value: 'partly', label: 'Partly' },
        { value: 'stuck', label: 'Stuck' },
      ],
      acoFilter: null,
      acoOptions: [
        { value: 'none', label: 'Not in an ACO' },
        { value: 'any', label: 'In an ACO' },
      ],
      sizeFilter: null,
      sizeOptions: [
        { value: 'small', label: 'Under 1,000' },
        { value: 'mid', label: '1,000 to 5,000' },
        { value: 'large', label: 'Over 5,000' },
      ],
      gridTheme: themeQuartz.withParams({
        browserColorScheme: 'light',
        headerBackgroundColor: '#F7F7F8',
        headerTextColor: '#626264',
        headerFontSize: 12,
        headerFontWeight: 600,
        fontSize: 13,
        rowBorder: true,
        wrapperBorder: false,
        wrapperBorderRadius: 0,
        cellHorizontalPadding: 16,
        rowHoverColor: '#F7F7F8',
      }),
      defaultColDef: {
        sortable: true,
        resizable: false,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        cellClass: 'cell-theme-left-aligned',
      },
      columnDefs: [
        { headerName: '#', field: 'rank', width: 56, sortable: false, cellClass: 'cell-theme-center-aligned text-gray-400 tabular-nums', headerClass: 'ag-center-aligned-header' },
        {
          headerName: 'Practice',
          field: 'name',
          flex: 2,
          minWidth: 240,
          cellRenderer: (p) => `<div class="leading-tight"><div class="font-semibold text-gray-900 sensitive">${escapeHtml(p.data.name)}</div><div class="text-[11px] text-gray-500 sensitive">${escapeHtml(p.data.place)}</div></div>`,
        },
        { headerName: 'Providers', field: 'providers', width: 110, type: 'numericColumn', cellClass: 'cell-theme-right-aligned tabular-nums' },
        {
          headerName: 'Medicare patients',
          field: 'lives',
          width: 150,
          type: 'numericColumn',
          cellClass: 'cell-theme-right-aligned tabular-nums',
          valueFormatter: (p) => (p.value == null ? '' : p.value.toLocaleString()),
        },
        {
          headerName: 'Upside per year',
          field: 'upside_dollars',
          width: 140,
          type: 'numericColumn',
          cellClass: 'cell-theme-right-aligned tabular-nums font-semibold text-gray-900',
          valueFormatter: (p) => fmtMoney(p.value),
        },
        {
          headerName: 'Can improve',
          field: 'verdict',
          width: 130,
          cellRenderer: (p) => `<span class="inline-flex items-center gap-2 font-medium ${p.value === 'stuck' ? 'text-gray-500' : 'text-gray-900'}"><span class="inline-block w-2 h-2 rounded-sm ${VERDICT_DOT[p.value] || 'bg-gray-200'}"></span>${VERDICT_LABEL[p.value] || ''}</span>`,
        },
        { headerName: 'Biggest gap', field: 'gap', flex: 1.5, minWidth: 200 },
        {
          headerName: 'ACO today',
          field: 'program',
          flex: 1.5,
          minWidth: 180,
          cellClass: 'cell-theme-left-aligned sensitive',
          cellRenderer: (p) => (p.value ? escapeHtml(p.value) : '<span class="text-gray-400">None</span>'),
        },
        {
          headerName: '',
          field: 'practice_key',
          width: 48,
          sortable: false,
          resizable: false,
          cellClass: 'cell-theme-center-aligned',
          cellRenderer: () => '<span class="text-gray-400" title="Read brief" aria-label="Read brief"><svg width="8" height="12" viewBox="0 0 8 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 1.5 6 6l-4.5 4.5"/></svg></span>',
        },
      ],
    }
  },
  computed: {
    territoryOptions() {
      return this.states.map((s) => ({ value: s, label: s }))
    },
    targetCount() {
      return this.rows.filter((r) => r.is_target).length
    },
    inAcoCount() {
      return this.rows.filter((r) => r.program).length
    },
    filtersActive() {
      return !this.targetsOnly || this.verdictFilter !== 'all' || this.acoFilter !== null || this.sizeFilter !== null
    },
    filtered() {
      return this.rows
        .filter((t) => {
          if (this.targetsOnly && !t.is_target) return false
          if (this.verdictFilter !== 'all' && t.verdict !== this.verdictFilter) return false
          if (this.acoFilter === 'none' && t.program) return false
          if (this.acoFilter === 'any' && !t.program) return false
          if (this.sizeFilter === 'small' && t.lives >= 1000) return false
          if (this.sizeFilter === 'mid' && (t.lives < 1000 || t.lives > 5000)) return false
          if (this.sizeFilter === 'large' && t.lives <= 5000) return false
          return true
        })
        .map((t, i) => ({ ...t, rank: i + 1 }))
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const params = this.territory ? { state: this.territory } : {}
        const { data } = await this.$axios.get('/api/growth/briefs/', { params })
        this.buildId = data.build_id || ''
        this.states = data.states || []
        this.rows = (data.rows || []).map((r) => ({
          ...r,
          place: [r.county, r.state].filter(Boolean).join(', '),
          gap: INITIATIVE_LABEL[r.top_initiative] || (r.top_initiative ? r.top_initiative : ''),
        }))
      } finally {
        this.loading = false
      }
    },
    onTerritory(v) {
      this.territory = v
      this.$emit('territory-change', v)
      this.load()
    },
    getRowId(p) {
      return p.data.practice_key
    },
    onRowClicked(e) {
      this.$emit('open-brief', e.data)
    },
    resetFilters() {
      this.verdictFilter = 'all'
      this.acoFilter = null
      this.sizeFilter = null
      this.targetsOnly = true
    },
  },
}
</script>

<style scoped>
.targets-grid :deep(.ag-center-cols-viewport) {
  min-height: 0 !important;
}
.targets-grid :deep(.ag-header-cell-text) {
  font-weight: 600;
}
.targets-grid :deep(.cell-theme-right-aligned) {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end;
  white-space: normal !important;
}
</style>
